// =====================================================================
// Faz J (2026-05-06) — RFM Müşteri Analizi
// =====================================================================
// RFM = Recency, Frequency, Monetary. Müşteriyi 3 ekseninde 1-5 arası
// skorlayıp 11 standart segment'e atayan endüstri-standart B2B aracı.
//
// Bu sayfa "kim VIP, kim kayboluyor, kime ne yapmalıyız" sorusunu cevaplar.
// Owner direkt aksiyon alabilir: Champions'a teşekkür kuponu, At-Risk'e
// reaktivasyon maili, Cannot-Lose listesini Mikail Bey arasın.
//
// Endpoint:
//   GET /api/admin/insights/rfm
//     -> { segments: [...], customers: [...], totals: {...} }
//
// 11 segment + sektör-uygun aksiyon önerileri backend'de gömülü
// (bkz: routes/admin-insights.js -> SEGMENT_META).
// =====================================================================
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, TextField,
  TableSortLabel, Pagination
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarIcon from "@mui/icons-material/Star";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import API from "../services/api";

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("tr-TR") : "—";

// Recency günü → kısa metin (insan-okur)
const recencyText = (days) => {
  if (days == null) return "—";
  if (days === 0) return "Bugün";
  if (days < 7) return `${days} gün`;
  if (days < 30) return `${Math.round(days / 7)} hafta`;
  if (days < 365) return `${Math.round(days / 30)} ay`;
  return `${Math.round(days / 365)} yıl`;
};

// Quintile skoru (1-5) → renkli chip
const ScoreChip = ({ score, axis }) => {
  // 5=koyu yeşil, 4=yeşil, 3=sarı, 2=turuncu, 1=kırmızı
  const colors = ["#94a3b8", "#ef4444", "#f97316", "#f59e0b", "#22c55e", "#10b981"];
  return (
    <Chip
      label={score}
      size="small"
      sx={{
        bgcolor: colors[score] || "#94a3b8",
        color: "white",
        fontWeight: 800,
        minWidth: 32,
        fontSize: 12,
        height: 22
      }}
      title={`${axis}: ${score}/5`}
    />
  );
};

const PAGE_SIZE = 25;

export default function RfmAnalysis() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [activeSegment, setActiveSegment] = useState(null);  // null = tümü
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("total_spent");        // total_spent | recency | frequency
  const [sortDir, setSortDir] = useState("desc");

  const load = () => {
    setLoading(true);
    API.get("/api/admin/insights/rfm")
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Filtrelenmiş + sıralı müşteri listesi
  const filteredCustomers = useMemo(() => {
    if (!data?.customers) return [];
    let list = data.customers;
    if (activeSegment) list = list.filter(c => c.segment_code === activeSegment);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(c =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.company_name || "").toLowerCase().includes(q)
      );
    }
    const dirMul = sortDir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      let va, vb;
      if (sortBy === "total_spent") { va = a.total_spent; vb = b.total_spent; }
      else if (sortBy === "recency") { va = a.recency_days; vb = b.recency_days; }
      else if (sortBy === "frequency") { va = a.order_count; vb = b.order_count; }
      else { va = a.total_spent; vb = b.total_spent; }
      return (va - vb) * dirMul;
    });
    return list;
  }, [data, activeSegment, search, sortBy, sortDir]);

  // Segment hızlı arama için map
  const segmentMap = useMemo(() => {
    if (!data?.segments) return {};
    return Object.fromEntries(data.segments.map(s => [s.code, s]));
  }, [data]);

  const pageCount = Math.ceil(filteredCustomers.length / PAGE_SIZE);
  const paged = filteredCustomers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const t = data.totals;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Hero */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>RFM Müşteri Analizi</Typography>
          <Typography variant="body2" color="text.secondary">
            Recency · Frequency · Monetary — son 365 gün, kim VIP / kim kayboluyor
          </Typography>
        </Box>
        <Tooltip title="Yenile">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* Toplam KPI */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Müşteri</Typography>
            <Typography variant="h4" fontWeight={800}>{t.customers}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Sipariş Veren</Typography>
            <Typography variant="h4" fontWeight={800} color="#10b981">{t.with_orders}</Typography>
            <Typography variant="caption" color="text.disabled">
              %{t.customers > 0 ? Math.round((t.with_orders / t.customers) * 100) : 0} aktivasyon
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Ciro (365 gün)</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0">{fmtMoney(t.revenue)}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Müşteri Başı Ortalama</Typography>
            <Typography variant="h4" fontWeight={800} color="#f59e0b">
              {fmtMoney(t.with_orders > 0 ? t.revenue / t.with_orders : 0)}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Segment grid — tıklanabilir filtre */}
      <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>
          11 RFM Segmenti — tıklayıp filtreleyin
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={4} md={3}>
            <Card
              onClick={() => { setActiveSegment(null); setPage(1); }}
              sx={{
                p: 1.5, cursor: "pointer", borderRadius: 2,
                border: activeSegment === null ? "2px solid #1565C0" : "2px solid transparent",
                bgcolor: activeSegment === null ? "#e3f2fd" : "#f8fafc",
                transition: "all 0.2s"
              }}
            >
              <Typography variant="caption" fontWeight={700} sx={{ color: "#64748b" }}>TÜMÜ</Typography>
              <Typography variant="h5" fontWeight={800}>{data.customers.length}</Typography>
              <Typography variant="caption" color="text.secondary">tüm aktif müşteriler</Typography>
            </Card>
          </Grid>
          {data.segments.map(s => (
            <Grid item xs={6} sm={4} md={3} key={s.code}>
              <Card
                onClick={() => { setActiveSegment(s.code); setPage(1); }}
                sx={{
                  p: 1.5, cursor: "pointer", borderRadius: 2,
                  border: activeSegment === s.code ? `2px solid ${s.color}` : "2px solid transparent",
                  bgcolor: activeSegment === s.code ? `${s.color}22` : "#f8fafc",
                  transition: "all 0.2s",
                  position: "relative"
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
                  {s.code === "champions" && <StarIcon sx={{ fontSize: 14, color: s.color }} />}
                  {s.code === "cannot_lose" && <LocalFireDepartmentIcon sx={{ fontSize: 14, color: s.color }} />}
                  <Typography variant="caption" fontWeight={700} sx={{ color: s.color, textTransform: "uppercase", fontSize: 11 }}>
                    {s.label}
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: s.color, lineHeight: 1.1 }}>
                  {s.count}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {fmtMoney(s.total_revenue)} · ort {recencyText(s.avg_recency_days)}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10.5, display: "block", mt: 0.4, lineHeight: 1.3 }}>
                  💡 {s.tip}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Card>

      {/* Müşteri tablosu */}
      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" fontWeight={700}>
            {activeSegment ? segmentMap[activeSegment]?.label : "Tüm Müşteriler"}
            <Chip label={filteredCustomers.length} size="small" sx={{ ml: 1 }} />
          </Typography>
          <Box sx={{ flex: 1 }} />
          <TextField
            size="small" placeholder="Müşteri ara (isim/firma/email)"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            sx={{ width: 280 }}
          />
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell><strong>Müşteri</strong></TableCell>
              <TableCell align="center"><strong>R</strong></TableCell>
              <TableCell align="center"><strong>F</strong></TableCell>
              <TableCell align="center"><strong>M</strong></TableCell>
              <TableCell><strong>Segment</strong></TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "recency"}
                  direction={sortBy === "recency" ? sortDir : "asc"}
                  onClick={() => toggleSort("recency")}
                >
                  <strong>Son Sipariş</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === "frequency"}
                  direction={sortBy === "frequency" ? sortDir : "asc"}
                  onClick={() => toggleSort("frequency")}
                >
                  <strong>Sip.</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === "total_spent"}
                  direction={sortBy === "total_spent" ? sortDir : "asc"}
                  onClick={() => toggleSort("total_spent")}
                >
                  <strong>Toplam Harcama</strong>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Bu kriterde müşteri yok.
                </TableCell>
              </TableRow>
            )}
            {paged.map(c => {
              const seg = segmentMap[c.segment_code];
              return (
                <TableRow key={c.user_id} hover>
                  <TableCell>
                    <Typography fontWeight={600} fontSize={14}>{c.company_name || c.name || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.email}</Typography>
                  </TableCell>
                  <TableCell align="center"><ScoreChip score={c.R} axis="Recency" /></TableCell>
                  <TableCell align="center"><ScoreChip score={c.F} axis="Frequency" /></TableCell>
                  <TableCell align="center"><ScoreChip score={c.M} axis="Monetary" /></TableCell>
                  <TableCell>
                    <Chip
                      label={seg?.label || c.segment_code}
                      size="small"
                      sx={{
                        bgcolor: (seg?.color || "#94a3b8") + "22",
                        color: seg?.color || "#64748b",
                        fontWeight: 700,
                        fontSize: 11
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={13}>{recencyText(c.recency_days)}</Typography>
                    <Typography variant="caption" color="text.disabled">{fmtDate(c.last_order_at)}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{c.order_count}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: "#1565C0" }}>
                    {fmtMoney(c.total_spent)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {pageCount > 1 && (
          <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
            <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="primary" />
          </Box>
        )}
      </Card>
    </Box>
  );
}
